import React from 'react';
import { Sparkles } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import { tx } from '@/translations/translations';

const MATERIALS = [
  {
    key: 'lana',
    name: { es: 'Lana', en: 'Wool', he: 'צמר' },
    accent: 'from-amber-100 to-amber-50',
    badge: 'bg-amber-900 text-amber-50',
    kashrut: 5,
    comfort: 3,
    warm: 3,
    care: 2,
    bestFor: { es: 'Uso diario', en: 'Daily use', he: 'שימוש יומיומי' },
    feature: { es: 'Cumple Kol Haposkim', en: 'Fulfills Kol Haposkim', he: 'יוצא ידי כל הפוסקים' },
  },
  {
    key: 'algodon',
    name: { es: 'Algodón', en: 'Cotton', he: 'כותנה' },
    accent: 'from-slate-100 to-white',
    badge: 'bg-slate-800 text-slate-50',
    kashrut: 4.5,
    comfort: 5,
    warm: 2,
    care: 4.5,
    bestFor: { es: 'Uso diario', en: 'Daily use', he: 'שימוש יומיומי' },
    feature: { es: 'Fresco y ligero', en: 'Cool and light', he: 'קליל ונעים' },
  },
  {
    key: 'bambu',
    name: { es: 'Bambú', en: 'Bamboo', he: 'במבוק' },
    accent: 'from-emerald-100 to-emerald-50',
    badge: 'bg-emerald-800 text-emerald-50',
    kashrut: 2.5,
    comfort: 5,
    warm: 3,
    care: 3,
    bestFor: { es: 'Piel sensible', en: 'Sensitive skin', he: 'עור רגיש' },
    feature: { es: 'Ultra suave · Eco', en: 'Ultra soft · Eco', he: 'רך במיוחד · אקולוגי' },
  },
  {
    key: 'dryfit',
    name: { es: 'Dry-Fit', en: 'Dry-Fit', he: 'דריי-פיט' },
    accent: 'from-cyan-100 to-cyan-50',
    badge: 'bg-cyan-800 text-cyan-50',
    kashrut: 2.5,
    comfort: 3.5,
    warm: 3,
    care: 4,
    bestFor: { es: 'Deporte y actividad', en: 'Sports and activity', he: 'ספורט ופעילות' },
    feature: { es: 'Absorbe sudor', en: 'Wicks sweat', he: 'סופג זיעה' },
  },
];

const Dots = ({ n }) => {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => {
        let fill;
        if (i <= n) fill = 'full';
        else if (i - 0.5 <= n) fill = 'half';
        else fill = 'empty';
        return (
          <span
            key={i}
            className={`w-1.5 h-1.5 rounded-full overflow-hidden ${
              fill === 'full' ? 'bg-slate-900' :
              fill === 'empty' ? 'bg-slate-200' :
              'bg-slate-200'
            }`}
          >
            {fill === 'half' && (
              <span className="block w-1/2 h-full bg-slate-900" />
            )}
          </span>
        );
      })}
    </div>
  );
};

const MaterialComparison = () => {
  const { language } = useLanguage();

  return (
    <section className="py-12 md:py-20 bg-slate-50" data-testid="material-comparison">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 bg-slate-900 text-white text-xs font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full mb-3">
            <Sparkles className="h-3.5 w-3.5" />
            {tx(language, 'Materiales', 'Materials', 'חומרים')}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">
            {tx(language, '¿Cuál es el tuyo?', 'Which one is yours?', 'איזה מתאים לך?')}
          </h2>
          <p className="text-sm text-slate-500 max-w-xl mx-auto">
            {tx(language, 'Compara y encuentra el tzitzit perfecto para tu estilo de vida', 'Compare and find the perfect tzitzit for your lifestyle', 'השוו ומצאו את הציצית המושלמת לסגנון החיים שלכם')}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
          {MATERIALS.map((m) => (
            <div
              key={m.key}
              data-testid={`material-card-${m.key}`}
              className="relative rounded-2xl bg-white overflow-hidden ring-1 ring-slate-200 hover:ring-slate-900/20 hover:shadow-lg transition-all group"
            >
              <div className={`h-20 md:h-24 bg-gradient-to-br ${m.accent} relative`}>
                <span className={`absolute bottom-2 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${m.badge}`}>
                  {m.name[language] || m.name.en}
                </span>
              </div>
              <div className="p-3 sm:p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {tx(language, 'Kashrut', 'Kashrut', 'כשרות')}
                  </span>
                  <Dots n={m.kashrut} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {tx(language, 'Suavidad', 'Softness', 'רכות')}
                  </span>
                  <Dots n={m.comfort} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {tx(language, 'Calor', 'Warmth', 'חום')}
                  </span>
                  <Dots n={m.warm} />
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider">
                    {tx(language, 'Fácil lavado', 'Easy wash', 'כביסה קלה')}
                  </span>
                  <Dots n={m.care} />
                </div>
                <div className="pt-2 border-t border-slate-100 space-y-1">
                  <p className="text-[10px] text-slate-400 uppercase tracking-wider">
                    {tx(language, 'Ideal para', 'Best for', 'אידיאלי ל')}
                  </p>
                  <p className="text-xs font-semibold text-slate-800 leading-tight">
                    {m.bestFor[language] || m.bestFor.en}
                  </p>
                  <p className="text-[11px] text-cyan-600 font-medium">{m.feature[language] || m.feature.en}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default MaterialComparison;
